import {
  Button,
  FiltersVisibilityButton,
  Loader,
  Table,
  TableColumn,
  TableColumnHiding,
  TablePagination,
  TableRow,
  icons,
  useToast
} from '@lifebit-ai/lds'
import { useQueryClient } from '@tanstack/react-query'
import { ResultsState, ResultsType } from 'enums'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { TablePaginationContainer, TableWrapper } from 'core/Common.styles'
import { extractErrorMessage } from 'core/utils'
import { useToggleState } from 'hooks/useToggleState'
import { AssigneeFilter } from 'modules/requests/AssigneeFilter'
import { ActionsContainer } from 'modules/requests/ExportAndAccessRequestsTable/ExportAndAccessRequestsTable.styles'
import {
  EmptyStateContainer,
  LoadingContainer
} from 'modules/requests/ExportAndAccessRequestsTable/RequestsTable.styles'
import * as styles from 'modules/requests/ExportAndAccessRequestsTable/RequestsTable.styles'
import {
  getColumnsForAnalysis,
  getColumnsForFilesAndFolders
} from 'modules/requests/ExportAndAccessRequestsTable/exportRequestsTableColumns'
import { FileNameFilter } from 'modules/requests/FileNameFilter'
import { LabelsFilter } from 'modules/requests/LabelsFilter'
import { RequestedByFilter } from 'modules/requests/RequestedByFilter'
import { WorkspacesFilter } from 'modules/requests/WorkspacesFilter'
import { RequestTypeFilter } from 'modules/requests/filters/RequestTypeFilter'
import { StatusFilter } from 'modules/requests/filters/StatusFilter'
import { ValidationStatusFilter } from 'modules/requests/filters/ValidationStatusFilter'
import { useExportRequestsToCSV } from 'modules/requests/hooks/useExportRequestsToCSV'
import {
  getExportAndAccessRequestDetailsPath,
  isAirlockRequestsV3RoutesEnabled
} from 'modules/requests/requestDetailPaths'
import {
  EXPORT_ONLY_REQUEST_TYPES_QUERY,
  RESULT_STATUS_REFETCH_INTERVAL_MS,
  shouldPollResultStatus
} from 'modules/requests/utils'
import AssignToModal from 'modules/resultsV2/AssignToModal'
import { DEFAULT_PAGE_SIZE } from 'services/results'
import { useBulkUnassignExportRequests } from 'services/results/hooks/useBulkUnassignExportRequests'
import { ResultsQueryKeys } from 'services/results/queryKeys'
import type { Result } from 'services/results/types'
import { useApproveResultV2 } from 'services/results/v2/hooks/useApproveResultV2'
import { useGetResultsVerificationV2 } from 'services/results/v2/hooks/useGetResultsVerificationV2'
import type {
  ApproveResultV2Params,
  ApproveResultV2Response,
  GetResultsVerificationV2Params,
  GetResultsVerificationV2Response,
  ResultsVerificationV2Filters
} from 'services/results/v2/types'

import { RequestedDateFilter } from '../RequestedDateFilter'
import RowHoverOverlay from './RowHoverOverlay'

export const EXPORT_REQUEST_TABLE_WRAPPER_TEST_ID = 'export-requests-table-wrapper-test-id'
export const LOADING_CONTAINER_TEST_ID = 'loading-container-test-id'
export const FILTERS_CONTAINER_TEST_ID = 'export-requests-filters-container-test-id'
export const EXPORT_CSV_BUTTON_TEST_ID = 'export-to-csv-button'

const LoadingIcon = () => {
  return (
    <LoadingContainer data-testid={LOADING_CONTAINER_TEST_ID}>
      <Loader text={false} />
    </LoadingContainer>
  )
}

interface ExportAndAccessRequestsTableProps {
  resultType: ResultsType
}

const ExportAndAccessRequestsTable: React.FC<ExportAndAccessRequestsTableProps> = ({ resultType }) => {
  const [tableState, setTableState] = useState<{ page: number; pageSize: number }>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE
  })

  const [filtersState, setFiltersState] = useState<
    Record<
      | 'workspaceIds'
      | 'search'
      | 'requesterIds'
      | 'assigneeIds'
      | 'status'
      | 'fromDate'
      | 'toDate'
      | 'validationStatus'
      | 'labelIds'
      | 'requestTypes',
      string[] | string
    >
  >({
    workspaceIds: [],
    search: '',
    requesterIds: [],
    assigneeIds: [],
    status: [],
    fromDate: '',
    toDate: '',
    validationStatus: '',
    labelIds: [],
    requestTypes: []
  })

  const [assignToResultId, setAssignToResultId] = useState<string | null>(null)
  const [areFiltersVisible, toggleFiltersVisibility] = useToggleState(true)
  const [columnHiding, setColumnHiding] = useState<string[]>([])

  const columnToFilterKeyMap: Record<string, (keyof typeof filtersState)[]> = {
    status: ['status'],
    name: ['search'],
    workspaceName: ['workspaceIds'],
    requestedBy: ['requesterIds'],
    requestDate: ['fromDate', 'toDate'],
    validationStatus: ['validationStatus'],
    labelIds: ['labelIds'],
    assignedTo: ['assigneeIds'],
    requestType: ['requestTypes']
  }

  const filterDefaults: Record<string, string[] | string> = {
    status: [],
    search: '',
    workspaceIds: [],
    requesterIds: [],
    fromDate: '',
    toDate: '',
    validationStatus: '',
    labelIds: [],
    assigneeIds: [],
    requestTypes: []
  }

  const hiding: TableColumnHiding = {
    current: columnHiding,
    onChange: (hiddenColumns: string[]) => {
      const newlyHidden = hiddenColumns.filter(col => !columnHiding.includes(col))

      if (newlyHidden.length) {
        const resetFields = newlyHidden.flatMap(col => columnToFilterKeyMap[col] ?? [])

        // Reset the newly hidden columns' filters so the user isn't visualizing data
        // influenced by a hidden column's filter.
        if (resetFields.length) {
          setFiltersState(prev => {
            const keyValuesArray = resetFields.map(key => [key, filterDefaults[key]])
            const updates = Object.fromEntries(keyValuesArray)
            return { ...prev, ...updates }
          })

          setTableState(prev => ({ ...prev, page: 0 }))
        }
      }

      setColumnHiding(hiddenColumns)
    }
  }

  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()

  // Queries
  const requestTypes = filtersState.requestTypes.length
    ? (filtersState.requestTypes as string[]).join(',')
    : isAirlockRequestsV3RoutesEnabled()
    ? EXPORT_ONLY_REQUEST_TYPES_QUERY
    : undefined

  const activeFilters: ResultsVerificationV2Filters = {
    resultType,
    ...(filtersState.workspaceIds.length ? { workspaceIds: (filtersState.workspaceIds as string[]).join(',') } : {}),
    ...(filtersState.search.length ? { search: filtersState.search as string } : {}),
    ...(filtersState.requesterIds.length ? { requesterIds: (filtersState.requesterIds as string[]).join(',') } : {}),
    ...(filtersState.assigneeIds.length ? { assigneeIds: (filtersState.assigneeIds as string[]).join(',') } : {}),
    ...(filtersState.status.length ? { status: (filtersState.status as string[]).join(',') } : {}),
    ...(filtersState.fromDate.length ? { fromDate: filtersState.fromDate as string } : {}),
    ...(filtersState.toDate.length ? { toDate: filtersState.toDate as string } : {}),
    ...(filtersState.validationStatus.length ? { validationStatus: filtersState.validationStatus as string } : {}),
    ...(filtersState.labelIds.length ? { labelIds: (filtersState.labelIds as string[]).join(',') } : {}),
    ...(requestTypes ? { requestTypes } : {})
  }

  const resultsVerificationV2Params: GetResultsVerificationV2Params = {
    page: tableState.page + 1,
    perPage: tableState.pageSize,
    ...activeFilters
  }

  const { data: paginatedResponse, isLoading: isLoadingResults } = useGetResultsVerificationV2(
    resultsVerificationV2Params,
    // Refetch while any request on screen is still settling server-side
    {
      refetchInterval: query =>
        query.state.data?.data.some(result => shouldPollResultStatus(result.status))
          ? RESULT_STATUS_REFETCH_INTERVAL_MS
          : false
    }
  )

  // The CSV holds every request matching the filters on screen, not just the current page.
  const { exportToCSV, isExporting } = useExportRequestsToCSV(activeFilters)

  const bulkUnassignExportRequestsMutation = useBulkUnassignExportRequests({
    onSuccess: () => {
      toast({ message: 'Export requests unassigned successfully', type: 'success' })
      queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.ResultsVerificationV2(resultsVerificationV2Params) })
      queryClient.invalidateQueries({ queryKey: ResultsQueryKeys.ResultsVerificationAssignees() })
    }
  })

  const approveResultV2Mutation = useApproveResultV2({
    onSuccess: (_data: ApproveResultV2Response, variables: { resultId: string; params: ApproveResultV2Params }) => {
      toast({ message: 'Export approval request sent', type: 'success' })

      const cachedVerificationV2Data = queryClient.getQueryData<GetResultsVerificationV2Response>(
        ResultsQueryKeys.ResultsVerificationV2(resultsVerificationV2Params)
      )

      if (!cachedVerificationV2Data) return

      // Optimistic update
      queryClient.setQueryData<GetResultsVerificationV2Response>(
        ResultsQueryKeys.ResultsVerificationV2(resultsVerificationV2Params),
        {
          ...cachedVerificationV2Data,
          data: cachedVerificationV2Data.data.map(result =>
            result._id === variables.resultId ? { ...result, status: ResultsState.Processing } : result
          )
        }
      )
    },
    onError: error => {
      toast({ message: extractErrorMessage(error, 'Failed to approve result'), type: 'error' })
    }
  })

  // Handlers
  const openResults = (resultId: string, result?: Result): void => {
    if (!resultId) return
    navigate(getExportAndAccessRequestDetailsPath(resultId, result))
  }

  const handleOnClickRetry = (row: TableRow<Result>): void => {
    approveResultV2Mutation.mutate({
      resultId: row.original._id,
      params: { downloadLinkExpiresInSeconds: row.original.downloadLinkExpiresInSeconds }
    })
  }

  const handleAssignTo = (resultId: string) => {
    setAssignToResultId(resultId)
  }

  const handleUnassignFrom = (resultId: string) => {
    bulkUnassignExportRequestsMutation.mutate({
      resultIds: [resultId]
    })
  }

  const activeFiltersCount = useMemo(
    () =>
      Object.entries(filtersState).filter(([, value]) => {
        return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== ''
      }).length,
    [filtersState]
  )

  // Render
  const columns: TableColumn<Result>[] =
    resultType === ResultsType.FilesFolders ? getColumnsForFilesAndFolders() : getColumnsForAnalysis()

  const columnIds = useMemo(() => new Set(columns.map(col => col.id)), [columns])
  const isColumnVisible = (id: string) => columnIds.has(id) && !columnHiding.includes(id)

  const pagination: TablePagination = {
    pageSize: tableState.pageSize,
    currentPage: tableState.page,
    totalRows: paginatedResponse?.pagination?.totalCount || 0,
    pageSizeOptions: [8, 10, 20, 50],
    onPageChange: page => {
      setTableState(prev => ({ ...prev, page }))
    },
    onPageSizeChange: pageSize => {
      setTableState(prev => ({ ...prev, page: 0, pageSize }))
    }
  }

  return (
    <TableWrapper data-testid={EXPORT_REQUEST_TABLE_WRAPPER_TEST_ID}>
      <Table<Result>
        rowKey="_id"
        data-testid="export-requests-table"
        columns={columns}
        data={paginatedResponse?.data ?? []}
        onRowClicked={(_, row) => openResults(row.original._id, row.original)}
        loading={isLoadingResults}
        loadingIcon={<LoadingIcon />}
        emptyComponent={<EmptyStateContainer>No results found</EmptyStateContainer>}
        rowHoverOverlay={row => (
          <RowHoverOverlay
            row={row}
            onClickRetry={() => handleOnClickRetry(row)}
            onAssignTo={handleAssignTo}
            onUnassignFrom={handleUnassignFrom}
            isRetrying={approveResultV2Mutation.isPending}
          />
        )}
        pagination={pagination}
        hiding={hiding}
      >
        <styles.FiltersVisibilityButtonWrapper>
          <FiltersVisibilityButton
            areFiltersVisible={areFiltersVisible}
            numActiveFilters={activeFiltersCount}
            onClick={toggleFiltersVisibility}
          />
          <Table.ColumnVisibilityToggler />
          <ActionsContainer>
            <Button
              data-testid={EXPORT_CSV_BUTTON_TEST_ID}
              loading={isExporting}
              disabled={!paginatedResponse?.data.length || isExporting}
              onClick={exportToCSV}
            >
              <icons.DownloadSmall />
              <span>Export to CSV</span>
            </Button>
          </ActionsContainer>
        </styles.FiltersVisibilityButtonWrapper>
        {areFiltersVisible && (
          <styles.FiltersContainer data-testid={FILTERS_CONTAINER_TEST_ID}>
            {isColumnVisible('status') && (
              <StatusFilter
                onApply={filteredOptions => {
                  setFiltersState(prev => ({ ...prev, status: filteredOptions.map(option => option.value) }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
                onReset={() => {
                  setFiltersState(prev => ({ ...prev, status: [] }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
              />
            )}
            {isColumnVisible('name') && (
              <FileNameFilter
                onApply={searchTerm => {
                  setFiltersState(prev => ({ ...prev, search: searchTerm }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
                onReset={() => {
                  setFiltersState(prev => ({ ...prev, search: '' }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
              />
            )}
            {isColumnVisible('labelIds') && (
              <LabelsFilter
                onApply={filteredOptions => {
                  setFiltersState(prev => ({ ...prev, labelIds: filteredOptions.map(option => option.value) }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
                onReset={() => {
                  setFiltersState(prev => ({ ...prev, labelIds: [] }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
              />
            )}
            {isColumnVisible('workspaceName') && (
              <WorkspacesFilter
                onApply={filteredOptions => {
                  setFiltersState(prev => ({ ...prev, workspaceIds: filteredOptions.map(option => option.value) }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
                onReset={() => {
                  setFiltersState(prev => ({ ...prev, workspaceIds: [] }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
              />
            )}
            {isColumnVisible('requestedBy') && (
              <RequestedByFilter
                onApply={filteredOptions => {
                  setFiltersState(prev => ({ ...prev, requesterIds: filteredOptions.map(option => option.value) }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
                onReset={() => {
                  setFiltersState(prev => ({ ...prev, requesterIds: [] }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
              />
            )}
            {isColumnVisible('requestType') && (
              <RequestTypeFilter
                onApply={filteredOptions => {
                  setFiltersState(prev => ({ ...prev, requestTypes: filteredOptions.map(option => option.value) }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
                onReset={() => {
                  setFiltersState(prev => ({ ...prev, requestTypes: [] }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
              />
            )}
            {isColumnVisible('requestDate') && (
              <RequestedDateFilter
                onApply={(dateStrings: string[]) => {
                  setFiltersState(prev => ({ ...prev, fromDate: dateStrings[0], toDate: dateStrings[1] }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
                onReset={() => {
                  setFiltersState(prev => ({ ...prev, fromDate: '', toDate: '' }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
              />
            )}
            {isColumnVisible('assignedTo') && (
              <AssigneeFilter
                onApply={filteredOptions => {
                  setFiltersState(prev => ({ ...prev, assigneeIds: filteredOptions.map(option => option.value) }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
                onReset={() => {
                  setFiltersState(prev => ({ ...prev, assigneeIds: [] }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
              />
            )}
            {isColumnVisible('validationStatus') && (
              <ValidationStatusFilter
                onApply={filteredOption => {
                  setFiltersState(prev => ({ ...prev, validationStatus: filteredOption?.value || '' }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
                onReset={() => {
                  setFiltersState(prev => ({ ...prev, validationStatus: '' }))
                  setTableState(prev => ({ ...prev, page: 0 }))
                }}
              />
            )}
          </styles.FiltersContainer>
        )}

        <Table.TableContainer>
          <Table.Body />
          {paginatedResponse?.data.length ? (
            <TablePaginationContainer>
              <Table.Pagination />
            </TablePaginationContainer>
          ) : null}
        </Table.TableContainer>
      </Table>
      {assignToResultId && (
        <AssignToModal
          resultId={assignToResultId}
          open={!!assignToResultId}
          onClose={() => setAssignToResultId(null)}
        />
      )}
    </TableWrapper>
  )
}

export default ExportAndAccessRequestsTable
